import { apiClient } from './api-client';
import type {
  Account,
  Category,
  CostCenter,
  Transaction,
  Transfer,
  ApiResponse,
  PaginatedResponse,
  AccountType,
  TransactionType,
  TransferStatus,
  CategoryType,
  TransactionAttachment,
  Invoice,
  InvoiceWithItems,
  InvoicableItem,
  ServiceType,
  Timesheet,
  TimesheetSummary,
  ActiveTimer,
  ContingencyFee,
  ContingencyFeeSummary,
  AnnualFee,
  BtgCredentials,
  BtgBoletoConfig,
  BtgWebhookRow,
  FinanceNotification
} from '../types/financeV2.types';

// ============= ACCOUNT TYPES =============
export interface CreateAccountDto {
  name: string;
  holderName: string;
  type: AccountType;
  initialBalance: number;
  trackingStartDate: string;
  bankName?: string;
  bankCode?: string;
  agency?: string;
  accountNumber?: string;
  managerName?: string;
  isBtgEnabled?: boolean;
}

export interface UpdateAccountDto {
  name?: string;
  holderName?: string;
  bankName?: string;
  bankCode?: string;
  agency?: string;
  accountNumber?: string;
  managerName?: string;
  isBtgEnabled?: boolean;
  isActive?: boolean;
}

export interface AccountFilters {
  search?: string;
  type?: AccountType;
  isActive?: boolean;
  minBalance?: number;
  maxBalance?: number;
  page?: number;
  limit?: number;
}

// ============= CATEGORY TYPES =============
export interface CreateCategoryDto {
  name: string;
  code: string;
  type: CategoryType;
  parentId?: string;
  color?: string;
  description?: string;
}

export interface UpdateCategoryDto {
  name?: string;
  description?: string;
  color?: string;
  isActive?: boolean;
}

export interface CategoryFilters {
  search?: string;
  type?: CategoryType;
  isActive?: boolean;
  parentId?: string;
  page?: number;
  limit?: number;
}

// ============= COST CENTER TYPES =============
export interface CreateCostCenterDto {
  name: string;
  code: string;
  description?: string;
  budget?: number;
}

export interface UpdateCostCenterDto {
  name?: string;
  description?: string;
  budget?: number;
  isActive?: boolean;
}

export interface CostCenterFilters {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

// ============= TRANSACTION TYPES =============
export interface CreateTransactionDto {
  description: string;
  amount: number;
  type: TransactionType;
  accountId: string;
  categoryId?: string;
  costCenterId?: string;
  clientId?: string;
  caseId?: string;
  dueDate: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface CreateRecurringTransactionDto {
  description: string;
  /** Valor de cada parcela (use `totalAmount` para ratear um total). */
  amount?: number;
  /** Total rateado em centavos entre as parcelas (soma exata, no servidor). */
  totalAmount?: number;
  type: TransactionType;
  accountId: string;
  categoryId?: string;
  costCenterId?: string;
  clientId?: string;
  caseId?: string;
  startDate: string; // Alterado de firstDueDate
  installments: number; // Alterado de recurrenceCount
  intervalType?: 'MONTHLY' | 'WEEKLY' | 'DAILY' | 'YEARLY'; // Alterado de recurrenceInterval
  intervalValue?: number;
  notes?: string;
  metadata?: Record<string, unknown>;
  tags?: string[];
}

export interface UpdateTransactionDto {
  description?: string;
  amount?: number;
  categoryId?: string;
  costCenterId?: string;
  dueDate?: string;
  notes?: string;
}

export interface PayTransactionDto {
  paidDate: string;
  /** Menor que o valor do lançamento = pagamento parcial (saldo restante continua em aberto). */
  paidAmount: number;
  notes?: string;
}

export interface CancelTransactionDto {
  reason: string;
}

/** Situação: PENDING = a vencer, OVERDUE = vencido, OPEN = em aberto (regra do servidor). */
export type TransactionStatusFilter = 'PENDING' | 'OVERDUE' | 'OPEN' | 'PAID' | 'CANCELLED';

export interface TransactionFilters {
  search?: string;
  type?: TransactionType;
  status?: TransactionStatusFilter;
  accountId?: string;
  categoryId?: string;
  costCenterId?: string;
  clientId?: string;
  caseId?: string;
  createdByKind?: 'HUMAN' | 'AGENT';
  needsConfirmation?: boolean;
  dueDateFrom?: string;
  dueDateTo?: string;
  paidDateFrom?: string;
  paidDateTo?: string;
  amountMin?: number;
  amountMax?: number;
  isRecurring?: boolean;
  recurrenceGroupId?: string;
  page?: number;
  limit?: number;
}

// ============= TRANSFER TYPES =============
export interface CreateTransferDto {
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  transferDate?: string;
  description: string;
}

export interface CancelTransferDto {
  reason: string;
}

export interface TransferFilters {
  search?: string;
  fromAccountId?: string;
  toAccountId?: string;
  status?: TransferStatus;
  transferDateFrom?: string;
  transferDateTo?: string;
  amountMin?: number;
  amountMax?: number;
  page?: number;
  limit?: number;
}

// ============= FINANCE V2 SERVICE =============
export const financeV2Service = {
  // ==================== ACCOUNTS ====================
  accounts: {
    list: async (filters?: AccountFilters): Promise<PaginatedResponse<Account>> => {
      const response = await apiClient.get('/api/finance-v2/accounts', { params: filters });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ account: Account }>> => {
      const response = await apiClient.get(`/api/finance-v2/accounts/${id}`);
      return response.data;
    },

    create: async (data: CreateAccountDto): Promise<ApiResponse<{ account: Account }>> => {
      const response = await apiClient.post('/api/finance-v2/accounts', data);
      return response.data;
    },

    update: async (id: string, data: UpdateAccountDto): Promise<ApiResponse<{ account: Account }>> => {
      const response = await apiClient.put(`/api/finance-v2/accounts/${id}`, data);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<{ account: Account }>> => {
      const response = await apiClient.delete(`/api/finance-v2/accounts/${id}`);
      return response.data;
    },
  },

  // ==================== CATEGORIES ====================
  categories: {
    list: async (filters?: CategoryFilters): Promise<PaginatedResponse<Category>> => {
      const response = await apiClient.get('/api/finance-v2/categories', { params: filters });
      return response.data;
    },

    getTree: async (): Promise<ApiResponse<{ categories: Category[] }>> => {
      const response = await apiClient.get('/api/finance-v2/categories/tree');
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ category: Category }>> => {
      const response = await apiClient.get(`/api/finance-v2/categories/${id}`);
      return response.data;
    },

    create: async (data: CreateCategoryDto): Promise<ApiResponse<{ category: Category }>> => {
      const response = await apiClient.post('/api/finance-v2/categories', data);
      return response.data;
    },

    update: async (id: string, data: UpdateCategoryDto): Promise<ApiResponse<{ category: Category }>> => {
      const response = await apiClient.put(`/api/finance-v2/categories/${id}`, data);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<{ category: Category }>> => {
      const response = await apiClient.delete(`/api/finance-v2/categories/${id}`);
      return response.data;
    },
  },

  // ==================== COST CENTERS ====================
  costCenters: {
    list: async (filters?: CostCenterFilters): Promise<PaginatedResponse<CostCenter>> => {
      const response = await apiClient.get('/api/finance-v2/cost-centers', { params: filters });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ costCenter: CostCenter }>> => {
      const response = await apiClient.get(`/api/finance-v2/cost-centers/${id}`);
      return response.data;
    },

    create: async (data: CreateCostCenterDto): Promise<ApiResponse<{ costCenter: CostCenter }>> => {
      const response = await apiClient.post('/api/finance-v2/cost-centers', data);
      return response.data;
    },

    update: async (id: string, data: UpdateCostCenterDto): Promise<ApiResponse<{ costCenter: CostCenter }>> => {
      const response = await apiClient.put(`/api/finance-v2/cost-centers/${id}`, data);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<{ costCenter: CostCenter }>> => {
      const response = await apiClient.delete(`/api/finance-v2/cost-centers/${id}`);
      return response.data;
    },
  },

  // ==================== TRANSACTIONS ====================
  transactions: {
    list: async (filters?: TransactionFilters): Promise<PaginatedResponse<Transaction>> => {
      const response = await apiClient.get('/api/finance-v2/transactions', { params: filters });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ transaction: Transaction }>> => {
      const response = await apiClient.get(`/api/finance-v2/transactions/${id}`);
      return response.data;
    },

    create: async (data: CreateTransactionDto): Promise<ApiResponse<{ transaction: Transaction }>> => {
      const response = await apiClient.post('/api/finance-v2/transactions', data);
      return response.data;
    },

    createRecurring: async (data: CreateRecurringTransactionDto): Promise<ApiResponse<{ transactions: Transaction[] }>> => {
      const response = await apiClient.post('/api/finance-v2/transactions/recurring', data);
      return response.data;
    },

    update: async (id: string, data: UpdateTransactionDto): Promise<ApiResponse<{ transaction: Transaction }>> => {
      const response = await apiClient.put(`/api/finance-v2/transactions/${id}`, data);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<{ transaction: Transaction }>> => {
      const response = await apiClient.delete(`/api/finance-v2/transactions/${id}`);
      return response.data;
    },

    pay: async (id: string, data: PayTransactionDto): Promise<ApiResponse<{ transaction: Transaction; remainder: Transaction | null }>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${id}/pay`, data);
      return response.data;
    },

    /** Sócio confirma lançamento criado pelo agente (Hermes). */
    confirm: async (id: string): Promise<ApiResponse<{ transaction: Transaction }>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${id}/confirm`);
      return response.data;
    },

    cancel: async (id: string, data: CancelTransactionDto): Promise<ApiResponse<{ transaction: Transaction }>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${id}/cancel`, data);
      return response.data;
    },
  },

  // ==================== TRANSFERS ====================
  transfers: {
    list: async (filters?: TransferFilters): Promise<PaginatedResponse<Transfer>> => {
      const response = await apiClient.get('/api/finance-v2/transfers', { params: filters });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ transfer: Transfer }>> => {
      const response = await apiClient.get(`/api/finance-v2/transfers/${id}`);
      return response.data;
    },

    create: async (data: CreateTransferDto): Promise<ApiResponse<{ transfer: Transfer }>> => {
      const response = await apiClient.post('/api/finance-v2/transfers', data);
      return response.data;
    },

    cancel: async (id: string, data: CancelTransferDto): Promise<ApiResponse<{ transfer: Transfer }>> => {
      const response = await apiClient.post(`/api/finance-v2/transfers/${id}/cancel`, data);
      return response.data;
    },
  },

  // ==================== ATTACHMENTS ====================
  attachments: {
    uploadSingle: async (transactionId: string, file: File): Promise<ApiResponse<TransactionAttachment>> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/api/finance-v2/transactions/${transactionId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    uploadMultiple: async (transactionId: string, files: File[]): Promise<ApiResponse<{ attachments: TransactionAttachment[]; errors?: string[] }>> => {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const response = await apiClient.post(`/api/finance-v2/transactions/${transactionId}/attachments/multiple`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    list: async (transactionId: string): Promise<ApiResponse<TransactionAttachment[]>> => {
      const response = await apiClient.get(`/api/finance-v2/transactions/${transactionId}/attachments`);
      return response.data;
    },

    download: async (attachmentId: string): Promise<Blob> => {
      const response = await apiClient.get(`/api/finance-v2/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },

    delete: async (attachmentId: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete(`/api/finance-v2/attachments/${attachmentId}`);
      return response.data;
    },

    myAttachments: async (): Promise<ApiResponse<TransactionAttachment[]>> => {
      const response = await apiClient.get('/api/finance-v2/attachments/my');
      return response.data;
    },
  },

  // ==================== BTG INTEGRATION ====================
  btg: {
    enableIntegration: async (accountId: string, credentials: {
      documentType: 'CPF' | 'CNPJ';
      documentNumber: string;
      companyName?: string;
      apiKey: string;
      clientId: string;
      clientSecret: string;
    }): Promise<ApiResponse<{ credentials: BtgCredentials }>> => {
      const response = await apiClient.post(`/api/finance-v2/accounts/${accountId}/btg/enable`, credentials);
      return response.data;
    },

    disableIntegration: async (accountId: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/accounts/${accountId}/btg/disable`);
      return response.data;
    },

    getBoletoConfig: async (accountId: string): Promise<ApiResponse<{ config: BtgBoletoConfig }>> => {
      const response = await apiClient.get(`/api/finance-v2/accounts/${accountId}/btg/config`);
      return response.data;
    },

    updateBoletoConfig: async (accountId: string, config: {
      defaultFinePercentage?: number;
      defaultInterestPercentage?: number;
      defaultDaysToDue?: number;
      defaultInstructions?: string;
      sendEmailOnIssue?: boolean;
      sendWhatsappOnIssue?: boolean;
    }): Promise<ApiResponse<{ config: BtgBoletoConfig }>> => {
      const response = await apiClient.put(`/api/finance-v2/accounts/${accountId}/btg/config`, config);
      return response.data;
    },

    syncTransactions: async (accountId: string, startDate?: string, endDate?: string): Promise<ApiResponse<{
      synced: number;
      created: number;
      updated: number;
      errors: string[];
    }>> => {
      const response = await apiClient.post(`/api/finance-v2/accounts/${accountId}/btg/sync`, null, {
        params: { startDate, endDate },
      });
      return response.data;
    },

    generateBoleto: async (transactionId: string, data: {
      accountId: string;
      amount: number;
      dueDate: string;
      description: string;
      payerDocument: string;
      payerName: string;
      payerEmail?: string;
      payerPhone?: string;
      finePercentage?: number;
      interestPercentage?: number;
      instructions?: string;
    }): Promise<ApiResponse<{ boleto: {
      boletoId: string;
      boletoUrl: string;
      barcodeNumber: string;
      digitableLine: string;
    } }>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${transactionId}/boleto`, data);
      return response.data;
    },

    getWebhooks: async (params?: {
      limit?: number;
      offset?: number;
      processed?: boolean;
      eventType?: string;
    }): Promise<ApiResponse<{
      webhooks: BtgWebhookRow[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/webhooks/btg', { params });
      return response.data;
    },

    cancelBoleto: async (transactionId: string, data: {
      accountId: string;
      reason?: string;
    }): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${transactionId}/boleto/cancel`, data);
      return response.data;
    },

    sendBoletoByEmail: async (transactionId: string, data: {
      accountId: string;
      recipientEmail: string;
    }): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${transactionId}/boleto/send-email`, data);
      return response.data;
    },

    sendBoletoByWhatsApp: async (transactionId: string, data: {
      accountId: string;
      recipientPhone: string;
    }): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/transactions/${transactionId}/boleto/send-whatsapp`, data);
      return response.data;
    },

    generateCarne: async (data: {
      accountId: string;
      transactionIds: string[];
    }): Promise<ApiResponse<{
      carne: {
        carneUrl: string;
        boletos: Array<{
          transactionId: string;
          boletoId: string;
          boletoUrl: string;
          installmentNumber: number;
          dueDate: string;
          amount: number;
        }>;
      };
    }>> => {
      const response = await apiClient.post('/api/finance-v2/carne/generate', data);
      return response.data;
    },

  },

  // Invoice endpoints
  invoices: {
    getInvoicableItems: async (clientId?: string): Promise<ApiResponse<{
      byClient: Record<string, InvoicableItem[]>;
      total: number;
    }>> => {
      const params = clientId ? { clientId } : {};
      const response = await apiClient.get('/api/finance-v2/invoices/to-invoice', { params });
      return response.data;
    },

    create: async (data: {
      client_id: string;
      items: Array<{
        item_type: 'FEE' | 'CLIENT_EXPENSE' | 'TIMESHEET';
        transaction_id?: string;
        timesheet_id?: string;
        description: string;
        quantity: number;
        unit_price: number;
        total: number;
      }>;
      discount?: number;
      due_date: string;
      notes?: string;
      payment_instructions?: string;
    }): Promise<ApiResponse<{ invoice: InvoiceWithItems }>> => {
      const response = await apiClient.post('/api/finance-v2/invoices', data);
      return response.data;
    },

    list: async (filters?: {
      client_id?: string;
      status?: 'OPEN' | 'PAID' | 'CANCELLED' | 'OVERDUE';
      from_date?: string;
      to_date?: string;
      search?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<{
      invoices: Invoice[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/invoices', { params: filters });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ invoice: InvoiceWithItems }>> => {
      const response = await apiClient.get(`/api/finance-v2/invoices/${id}`);
      return response.data;
    },

    update: async (id: string, data: {
      due_date?: string;
      discount?: number;
      notes?: string;
      payment_instructions?: string;
    }): Promise<ApiResponse<{ invoice: InvoiceWithItems }>> => {
      const response = await apiClient.put(`/api/finance-v2/invoices/${id}`, data);
      return response.data;
    },

    pay: async (id: string, data: {
      paid_date: string;
      paid_amount?: number;
      notes?: string;
    }): Promise<ApiResponse<{ invoice: InvoiceWithItems }>> => {
      const response = await apiClient.post(`/api/finance-v2/invoices/${id}/pay`, data);
      return response.data;
    },

    issueBoleto: async (id: string, data: {
      account_id: string;
      payer_name?: string;
      payer_document?: string;
      payer_email?: string;
      payer_phone?: string;
      fine_percentage?: number;
      interest_percentage?: number;
      instructions?: string;
    }): Promise<ApiResponse<{ invoice: InvoiceWithItems }>> => {
      const response = await apiClient.post(`/api/finance-v2/invoices/${id}/issue-boleto`, data);
      return response.data;
    },

    dismantle: async (id: string, data?: {
      reason?: string;
    }): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/invoices/${id}/dismantle`, data || {});
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete(`/api/finance-v2/invoices/${id}`);
      return response.data;
    },
  },

  // ============================================================
  // SERVICE TYPES
  // ============================================================
  serviceTypes: {
    getAll: async (params?: {
      search?: string;
      is_active?: boolean;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<{
      service_types: ServiceType[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/service-types', { params });
      return response.data;
    },

    getAllActive: async (): Promise<ApiResponse<{ service_types: ServiceType[] }>> => {
      const response = await apiClient.get('/api/finance-v2/service-types/active');
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ service_type: ServiceType }>> => {
      const response = await apiClient.get(`/api/finance-v2/service-types/${id}`);
      return response.data;
    },

    create: async (data: {
      name: string;
      description?: string;
      hourly_rate: number;
      is_active?: boolean;
    }): Promise<ApiResponse<{ service_type: ServiceType }>> => {
      const response = await apiClient.post('/api/finance-v2/service-types', data);
      return response.data;
    },

    update: async (id: string, data: {
      name?: string;
      description?: string;
      hourly_rate?: number;
      is_active?: boolean;
    }): Promise<ApiResponse<{ service_type: ServiceType }>> => {
      const response = await apiClient.put(`/api/finance-v2/service-types/${id}`, data);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete(`/api/finance-v2/service-types/${id}`);
      return response.data;
    },
  },

  // ============================================================
  // TIMESHEETS
  // ============================================================
  timesheets: {
    getAll: async (params?: {
      search?: string;
      user_id?: string;
      client_id?: string;
      case_id?: string;
      service_type_id?: string;
      status?: 'PENDING' | 'INVOICED' | 'CANCELLED';
      from_date?: string;
      to_date?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<{
      timesheets: Timesheet[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/timesheets', { params });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ timesheet: Timesheet }>> => {
      const response = await apiClient.get(`/api/finance-v2/timesheets/${id}`);
      return response.data;
    },

    create: async (data: {
      service_type_id: string;
      client_id: string;
      case_id?: string;
      work_date: string;
      duration_minutes: number;
      description: string;
      notes?: string;
    }): Promise<ApiResponse<{ timesheet: Timesheet }>> => {
      const response = await apiClient.post('/api/finance-v2/timesheets', data);
      return response.data;
    },

    update: async (id: string, data: {
      service_type_id?: string;
      client_id?: string;
      case_id?: string;
      work_date?: string;
      duration_minutes?: number;
      description?: string;
      notes?: string;
    }): Promise<ApiResponse<{ timesheet: Timesheet }>> => {
      const response = await apiClient.put(`/api/finance-v2/timesheets/${id}`, data);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete(`/api/finance-v2/timesheets/${id}`);
      return response.data;
    },

    cancel: async (id: string): Promise<ApiResponse<{ timesheet: Timesheet }>> => {
      const response = await apiClient.post(`/api/finance-v2/timesheets/${id}/cancel`);
      return response.data;
    },

    getSummary: async (params?: {
      from_date?: string;
      to_date?: string;
    }): Promise<ApiResponse<{ summary: TimesheetSummary }>> => {
      const response = await apiClient.get('/api/finance-v2/timesheets/summary', { params });
      return response.data;
    },
  },

  // ============================================================
  // TIMER
  // ============================================================
  timer: {
    getActive: async (): Promise<ApiResponse<{ timer: ActiveTimer | null }>> => {
      const response = await apiClient.get('/api/finance-v2/timesheets/timer/active');
      return response.data;
    },

    start: async (data: {
      service_type_id: string;
      client_id?: string;
      case_id?: string;
      description: string;
    }): Promise<ApiResponse<{ timer: ActiveTimer }>> => {
      const response = await apiClient.post('/api/finance-v2/timesheets/timer/start', data);
      return response.data;
    },

    stop: async (data?: {
      description?: string;
      notes?: string;
    }): Promise<ApiResponse<{ timesheet: Timesheet }>> => {
      const response = await apiClient.post('/api/finance-v2/timesheets/timer/stop', data || {});
      return response.data;
    },

    pause: async (): Promise<ApiResponse<{ timer: ActiveTimer }>> => {
      const response = await apiClient.post('/api/finance-v2/timesheets/timer/pause');
      return response.data;
    },

    resume: async (): Promise<ApiResponse<{ timer: ActiveTimer }>> => {
      const response = await apiClient.post('/api/finance-v2/timesheets/timer/resume');
      return response.data;
    },
  },

  // ============================================================
  // CONTINGENCY FEES
  // ============================================================
  contingencyFees: {
    list: async (params?: {
      case_id?: string;
      client_id?: string;
      status?: 'ACTIVE' | 'COLLECTED' | 'CANCELLED';
      cost_center_id?: string;
      from_date?: string;
      to_date?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<{
      fees: ContingencyFee[];
      pagination: { total: number; page: number; limit: number; totalPages: number };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/contingency-fees', { params });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ fee: ContingencyFee }>> => {
      const response = await apiClient.get(`/api/finance-v2/contingency-fees/${id}`);
      return response.data;
    },

    getByCase: async (caseId: string): Promise<ApiResponse<{ fees: ContingencyFee[] }>> => {
      const response = await apiClient.get(`/api/finance-v2/contingency-fees/case/${caseId}`);
      return response.data;
    },

    create: async (data: {
      case_id: string;
      client_id: string;
      percentage: number;
      description: string;
      cost_center_id?: string;
      alert_on_case_closure?: boolean;
    }): Promise<ApiResponse<{ fee: ContingencyFee }>> => {
      const response = await apiClient.post('/api/finance-v2/contingency-fees', data);
      return response.data;
    },

    update: async (id: string, data: {
      percentage?: number;
      description?: string;
      cost_center_id?: string;
      alert_on_case_closure?: boolean;
    }): Promise<ApiResponse<{ fee: ContingencyFee }>> => {
      const response = await apiClient.put(`/api/finance-v2/contingency-fees/${id}`, data);
      return response.data;
    },

    cancel: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/contingency-fees/${id}/cancel`);
      return response.data;
    },

    collect: async (id: string, data: {
      case_outcome_value: number;
      collected_value?: number;
      account_id: string;
      payment_date: string;
      notes?: string;
    }): Promise<ApiResponse<{ fee: ContingencyFee; transaction: Transaction }>> => {
      const response = await apiClient.post(`/api/finance-v2/contingency-fees/${id}/collect`, data);
      return response.data;
    },

    getSummary: async (params?: {
      client_id?: string;
      cost_center_id?: string;
    }): Promise<ApiResponse<ContingencyFeeSummary>> => {
      const response = await apiClient.get('/api/finance-v2/contingency-fees/summary', { params });
      return response.data;
    },

    // Hard Delete (Super Admin only)
    hardDelete: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete(`/api/finance-v2/contingency-fees/${id}/hard-delete`);
      return response.data;
    },
  },

  // ============================================================
  // NOTIFICATIONS
  // ============================================================
  notifications: {
    list: async (params?: {
      type?: string;
      read?: boolean;
      priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
      from_date?: string;
      to_date?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<{
      notifications: FinanceNotification[];
      pagination: {
        total: number;
        unread_count: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/notifications', { params });
      return response.data;
    },

    getUnreadCount: async (): Promise<ApiResponse<{ count: number }>> => {
      const response = await apiClient.get('/api/finance-v2/notifications/unread-count');
      return response.data;
    },

    markAsRead: async (notificationIds: string[]): Promise<ApiResponse<void>> => {
      const response = await apiClient.post('/api/finance-v2/notifications/mark-read', {
        notification_ids: notificationIds,
      });
      return response.data;
    },

    markAllAsRead: async (): Promise<ApiResponse<void>> => {
      const response = await apiClient.post('/api/finance-v2/notifications/mark-all-read');
      return response.data;
    },

    acknowledge: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.post(`/api/finance-v2/notifications/${id}/acknowledge`);
      return response.data;
    },

    delete: async (id: string): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete(`/api/finance-v2/notifications/${id}`);
      return response.data;
    },

    deleteAllRead: async (): Promise<ApiResponse<void>> => {
      const response = await apiClient.delete('/api/finance-v2/notifications/read');
      return response.data;
    },
  },

  // ============================================================
  // ANNUAL FEES
  // ============================================================
  annualFees: {
    create: async (data: {
      client_id: string;
      account_id: string;
      cost_center_id?: string;
      category_id?: string;
      year: number;
      total_amount: number;
      installments?: number;
      issue_date: string;
      first_due_date: string;
      apply_late_fee?: boolean;
      late_fee_percentage?: number;
      discount_percentage?: number;
      description?: string;
      notes?: string;
      generate_installments?: boolean;
    }): Promise<ApiResponse<{ annual_fee: AnnualFee }>> => {
      const response = await apiClient.post('/api/finance-v2/annual-fees', data);
      return response.data;
    },

    list: async (params?: {
      client_id?: string;
      year?: number;
      status?: 'PENDING' | 'ACTIVE' | 'PAID' | 'CANCELLED';
      account_id?: string;
      cost_center_id?: string;
      from_date?: string;
      to_date?: string;
      search?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<{
      annual_fees: AnnualFee[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/annual-fees', { params });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<{ annual_fee: AnnualFee }>> => {
      const response = await apiClient.get(`/api/finance-v2/annual-fees/${id}`);
      return response.data;
    },

    update: async (id: string, data: {
      account_id?: string;
      cost_center_id?: string;
      category_id?: string;
      total_amount?: number;
      issue_date?: string;
      first_due_date?: string;
      apply_late_fee?: boolean;
      late_fee_percentage?: number;
      discount_percentage?: number;
      description?: string;
      notes?: string;
    }): Promise<ApiResponse<{ annual_fee: AnnualFee }>> => {
      const response = await apiClient.put(`/api/finance-v2/annual-fees/${id}`, data);
      return response.data;
    },

    generateInstallments: async (id: string): Promise<ApiResponse<{
      success: boolean;
      annual_fee_id: string;
      transactions_created: number;
      recurrence_group_id: string;
      total_amount: number;
      message: string;
    }>> => {
      const response = await apiClient.post(`/api/finance-v2/annual-fees/${id}/generate-installments`);
      return response.data;
    },

    getSummary: async (id: string): Promise<ApiResponse<{
      summary: {
        annual_fee_id: string;
        total_amount: number;
        total_installments: number;
        paid_installments: number;
        pending_installments: number;
        overdue_installments: number;
        cancelled_installments: number;
        total_paid: number;
        total_pending: number;
        total_overdue: number;
        completion_percentage: number;
      };
    }>> => {
      const response = await apiClient.get(`/api/finance-v2/annual-fees/${id}/summary`);
      return response.data;
    },

    cancel: async (id: string, cancelPendingOnly: boolean = true): Promise<ApiResponse<{
      success: boolean;
      annual_fee_id: string;
      transactions_cancelled: number;
      message: string;
    }>> => {
      const response = await apiClient.post(`/api/finance-v2/annual-fees/${id}/cancel`, {
        cancel_pending_only: cancelPendingOnly,
      });
      return response.data;
    },

    getStatistics: async (year?: number): Promise<ApiResponse<{
      statistics: {
        total_annual_fees: number;
        total_amount_contracted: number;
        total_amount_received: number;
        total_amount_pending: number;
        by_status: {
          pending: number;
          active: number;
          paid: number;
          cancelled: number;
        };
        by_year: Array<{
          year: number;
          count: number;
          total_amount: number;
          total_received: number;
        }>;
        top_clients: Array<{
          client_id: string;
          client_name: string;
          total_annual_fees: number;
          total_amount: number;
        }>;
      };
    }>> => {
      const response = await apiClient.get('/api/finance-v2/annual-fees/statistics', {
        params: year ? { year } : undefined,
      });
      return response.data;
    },
  },

  // ============================================================
  // REPORTS
  // ============================================================
  reports: {
    getCollectionDashboard: async (): Promise<ApiResponse<{
      statistics: {
        overdue: { count: number; total: number };
        dueSoon: { count: number; total: number };
        pending: { count: number; total: number };
      };
      overdue: Array<{
        id: string;
        description: string;
        amount: number;
        dueDate: string;
        daysOverdue: number;
        clientId?: string;
        clientName?: string;
        clientDocument?: string;
        categoryName?: string;
        priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
      }>;
      dueSoon: Array<{
        id: string;
        description: string;
        amount: number;
        dueDate: string;
        daysUntilDue: number;
        clientId?: string;
        clientName?: string;
        clientDocument?: string;
        categoryName?: string;
      }>;
      topDebtors: Array<{
        clientId: string;
        clientName: string;
        overdueCount: number;
        overdueTotal: number;
        oldestDueDate: string;
        maxDaysOverdue: number;
      }>;
    }>> => {
      const response = await apiClient.get('/api/finance-v2/reports/collection-dashboard');
      return response.data;
    },
  },
};
